import { CallLog, CometChatCalls } from '@cometchat/calls-sdk-javascript';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import './History.css';
import { Avatar, CallDetails } from './components';
import { AccountMenu } from './components/AccountMenu/AccountMenu';
import { MdIcon } from './components/Icon/MdIcon';
import type { CallRecord } from './types/history';

const PAGE_SIZE = 10;

// The SDK's CallLogRequest is a stateful pagination cursor — keep a single
// instance for the screen and drive it with fetchNext()/fetchPrevious().
type CallLogRequest = ReturnType<
  InstanceType<typeof CometChatCalls.CallLogRequestBuilder>['build']
>;

// The list UI is written against the plain CallRecord shape, so unwrap the
// SDK's model objects once here instead of sprinkling getters through the JSX.
function toCallRecord(log: CallLog): CallRecord {
  return {
    sessionId: log.getSessionID(),
    mid: log.getMid(),
    receiverType: log.getReceiverType(),
    totalDuration: log.getTotalDuration(),
    totalDurationInMinutes: log.getTotalDurationInMinutes(),
    totalAudioMinutes: log.getTotalAudioMinutes(),
    totalVideoMinutes: log.getTotalVideoMinutes(),
    hasRecording: log.getHasRecording(),
    // The calls host sends `mode`/`startedAt`; older payloads send the
    // `callCategory`/`initiatedAt` equivalents.
    mode: log.getMode() ?? log.getCallCategory(),
    startedAt: log.getStartedAt() ?? log.getInitiatedAt(),
    endedAt: log.getEndedAt(),
    status: log.getStatus(),
    type: log.getType(),
    totalParticipants: log.getTotalParticipants(),
    participants: (log.getParticipants() ?? []).map(p => ({
      uid: p.getUid(),
      mid: p.getMid(),
      name: p.getName(),
      avatar: p.getAvatar(),
      deviceId: p.getDeviceID(),
      totalDurationInMinutes: p.getTotalDurationInMinutes(),
      totalAudioMinutes: p.getTotalAudioMinutes(),
      totalVideoMinutes: p.getTotalVideoMinutes(),
      joinedAt: p.getJoinedAt(),
      leftAt: p.getLeftAt(),
      state: p.getState(),
      isJoined: p.getHasJoined(),
    })),
    recordings: (log.getRecordings() ?? []).map(r => ({
      rid: r.getRid(),
      recording_url: r.getRecordingURL(),
      duration: r.getDuration(),
      startTime: r.getStartTime(),
      endTime: r.getEndTime(),
    })),
    transcriptions: (log.getTranscriptions() ?? []).map(t => ({
      tid: t.getTid(),
      mid: t.getMid(),
      roomName: t.getRoomName(),
      startTime: t.getStartTime(),
      endTime: t.getEndTime(),
      transcriptDate: t.getTranscriptDate(),
      transcriptUrl: t.getTranscriptURL(),
    })),
  };
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}


/**
 * What kind of entry a log is, by Android's HistoryCallAction table:
 * mode "call" + receiverType "user" is a 1:1 call (voice or video by type),
 * "call" + "group" a group call, "meet" (or a legacy log with no mode) a
 * meeting.
 */
function kindOf(call: CallRecord): string {
  if ((call.mode || '').toLowerCase() !== 'call') return 'Meeting';
  if ((call.receiverType || '').toLowerCase() === 'group') return 'Group call';
  return (call.type || '').toLowerCase() === 'audio' ? 'Voice call' : 'Video call';
}

/** The log's status as a label and a tone for its dot. */
function statusOf(status: string): { label: string; tone: 'live' | 'done' | 'miss' } {
  switch ((status || '').toLowerCase()) {
    case 'ongoing':
      return { label: 'Live', tone: 'live' };
    case 'ended':
      return { label: 'Ended', tone: 'done' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'miss' };
    case 'unanswered':
      return { label: 'No answer', tone: 'miss' };
    case 'missed':
      return { label: 'Missed', tone: 'miss' };
    case 'rejected':
      return { label: 'Declined', tone: 'miss' };
    case 'busy':
      return { label: 'Busy', tone: 'miss' };
    case 'initiated':
      return { label: 'Not connected', tone: 'miss' };
    default:
      return { label: status || 'Unknown', tone: 'done' };
  }
}

function History({ onLogout }: { onLogout: () => void }) {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  // On wide screens History also shows for `/` and invite links, beside the
  // lobby. Only /history owns the query string; elsewhere writing ?page=
  // would wipe an invite's ?meetingName=.
  const ownsUrl = pathname.startsWith('/history');
  // The SDK request walks pages one step at a time, so the URL reflects where
  // the cursor is rather than driving it — a reload always restarts at page 1.
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [paginationDir, setPaginationDir] = useState<'prev' | 'next' | null>(
    null,
  );
  const [selectedMid, setSelectedMid] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedCall = calls.find(c => c.mid === selectedMid) ?? null;

  const requestRef = useRef<CallLogRequest | null>(null);
  // A page fetch is under way, so the cursor is mid-step.
  const fetchingRef = useRef(false);

  // Walks the SDK cursor one page in `dir` and renders the result. A fresh
  // cursor's first page always applies (an empty one is a genuinely empty
  // history) and replaces the rows quietly, keeping any open call; later pages
  // treat an empty result as "cursor is at the boundary" and leave the rows on
  // screen.
  const loadPage = useCallback(async (dir: 'next' | 'prev') => {
    const request = requestRef.current;
    if (!request) return;
    const isFirstLoad = request.getCurrentPage() === 0;
    if (!isFirstLoad) {
      setPaginationDir(dir);
      setIsPaginating(true);
    }
    fetchingRef.current = true;
    try {
      const logs =
        dir === 'next'
          ? await request.fetchNext()
          : await request.fetchPrevious();
      if (logs.length === 0 && !isFirstLoad) return;
      setCalls(logs.map(toCallRecord));
      if (!isFirstLoad) setSelectedMid(null);
      setCurrentPage(Math.max(1, request.getCurrentPage()));
      setTotalPages(Math.max(1, request.getTotalPages()));
    } catch (err) {
      // fetchNext()/fetchPrevious() reject with a typed CometChatCallsException.
      // The rows already on screen (if any) stay.
      console.error('Failed to fetch call history:', err);
    } finally {
      fetchingRef.current = false;
      setIsInitialLoad(false);
      setIsPaginating(false);
      setPaginationDir(null);
    }
  }, []);

  // Pull the first page on a new cursor. Runs on the first visit, and again
  // each time the tab is shown: the shell keeps this screen in an <Activity>
  // (AppShell's ListPanes), which re-runs effects on show, so the rows from
  // last time stay up while the fresh page loads behind them. The fetching
  // check also absorbs StrictMode's double-invoke.
  useEffect(() => {
    if (fetchingRef.current) return;
    // Turned to a later page: stay on it. New calls land on page 1.
    if ((requestRef.current?.getCurrentPage() ?? 0) > 1) return;
    requestRef.current = new CometChatCalls.CallLogRequestBuilder()
      .setLimit(PAGE_SIZE)
      .setAuthToken(CometChatCalls.getUserAuthToken() || '')
      .build();
    loadPage('next');
  }, [loadPage]);

  // Keep the URL in step with the cursor so the page number survives in-app
  // navigation; `replace` avoids stacking a history entry per page turn.
  useEffect(() => {
    if (!ownsUrl) return;
    setSearchParams({ page: String(currentPage) }, { replace: true });
  }, [currentPage, setSearchParams, ownsUrl]);

  // Keys act on the list only: it now sits beside the lobby, whose fields and
  // controls need the arrow keys. Up/Down (and Home/End) move between rows,
  // Enter opens one (rows are buttons), Left/Right turn the page.
  const onListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('.md-list-item') ?? [],
    );
    const at = rows.indexOf(document.activeElement as HTMLButtonElement);
    const focusRow = (i: number) => rows[(i + rows.length) % rows.length]?.focus();
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusRow(at + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusRow(at < 0 ? rows.length - 1 : at - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        focusRow(rows.length - 1);
        break;
      case 'ArrowRight':
        if (currentPage < totalPages && !isPaginating) {
          e.preventDefault();
          loadPage('next');
        }
        break;
      case 'ArrowLeft':
        if (currentPage > 1 && !isPaginating) {
          e.preventDefault();
          loadPage('prev');
        }
        break;
    }
  };

  return (
    <section className="md-history" aria-label="Call history">
      {/* Compact widths only. On wide screens the pane's tabs name this list
          and the pane header carries the account menu. */}
      <header className="md-history__appbar">
        <h1 className="md-history__title">History</h1>
        <AccountMenu onLogout={onLogout} />
      </header>

      {isInitialLoad ? (
        <div className="md-history__state" role="status">
          <span className="md-progress" aria-hidden="true" />
          <span>Loading call history</span>
        </div>
      ) : calls.length === 0 ? (
        <div className="md-history__state">
          <span className="md-history__state-icon">
            <MdIcon name="history" size={32} />
          </span>
          <span className="md-history__state-title">No calls yet</span>
          <span>Calls you join show up here, with their recordings and transcripts.</span>
        </div>
      ) : (
        <>
          {/* The previous table's columns, as a caption strip. */}
          <div className="history-columns" aria-hidden="true">
            <span>Call</span>
            <span>Participants</span>
          </div>
          <ul
            ref={listRef}
            className="md-list"
            aria-label="Calls"
            aria-busy={isPaginating}
            onKeyDown={onListKeyDown}>
            {calls.map(call => {
              const hasRecording = (call.recordings?.length ?? 0) > 0;
              const hasTranscript = (call.transcriptions?.length ?? 0) > 0;
              const selected = selectedMid === call.mid;
              const status = statusOf(call.status);
              return (
                <li key={call.mid}>
                  {/* The previous table row, folded to two columns for the
                      pane: date / time / session id on the left, the avatar
                      stack, count and duration on the right. */}
                  <button
                    type="button"
                    className={`md-list-item history-row${selected ? ' history-row--selected' : ''}`}
                    aria-haspopup="dialog"
                    onClick={() => setSelectedMid(call.mid)}>
                    <span className="history-row__call">
                      <span className="history-row__line">
                        <span className="history-date-primary">
                          {formatDate(call.startedAt)}
                        </span>
                        <span className="history-kind">{kindOf(call)}</span>
                      </span>
                      <span className="history-row__line">
                        <span className="history-date-secondary">
                          {formatTime(call.startedAt)}
                        </span>
                        <span className={`history-status history-status--${status.tone}`}>
                          <span className="history-status__dot" aria-hidden="true" />
                          {status.label}
                        </span>
                      </span>
                      <span className="history-session-cell">
                        <span className="history-session-id" title={call.sessionId}>
                          {call.sessionId.split('.').pop()}
                        </span>
                        {hasRecording && (
                          <span className="history-recording-indicator" title="Has a recording">
                            <span className="md-visually-hidden">Has a recording</span>
                          </span>
                        )}
                        {hasTranscript && (
                          <span className="history-transcript-indicator" title="Has a transcript">
                            <span className="md-visually-hidden">Has a transcript</span>
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="history-row__people">
                      <span className="history-participants">
                        <span className="history-participant-avatars">
                          {call.participants.slice(0, 3).map((p, i) => (
                            <span key={`${p.uid}-${i}`} className="history-participant-avatar">
                              <Avatar name={p.name} imageUrl={p.avatar} size={30} />
                            </span>
                          ))}
                        </span>
                        <span className="history-participant-count">
                          {call.totalParticipants}
                        </span>
                      </span>
                      <span className="history-duration">{call.totalDuration}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <footer className="md-history__pager">
            <span className="md-history__page" aria-live="polite">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="md-icon-button"
              disabled={currentPage <= 1 || isPaginating}
              onClick={() => loadPage('prev')}
              aria-label="Previous page">
              {isPaginating && paginationDir === 'prev' ? (
                <span className="md-progress md-progress--small" aria-hidden="true" />
              ) : (
                <MdIcon name="chevron_left" />
              )}
            </button>
            <button
              type="button"
              className="md-icon-button"
              disabled={currentPage >= totalPages || isPaginating}
              onClick={() => loadPage('next')}
              aria-label="Next page">
              {isPaginating && paginationDir === 'next' ? (
                <span className="md-progress md-progress--small" aria-hidden="true" />
              ) : (
                <MdIcon name="chevron_right" />
              )}
            </button>
          </footer>
        </>
      )}

      {selectedCall && (
        <CallDetails call={selectedCall} onClose={() => setSelectedMid(null)} />
      )}
    </section>
  );
}

export default History;
